import { WEB_ICO } from './icons';
import { WebModal } from './WebModal';
import { ErrorState, LoadingState } from '../components/ScreenState';
import type { LessonSummary, UnitDetail } from '../api/content.types';

const TRACK_LABEL: Record<string, string> = { KANA: 'Ký tự', VOCAB: 'Từ mới', GRAMMAR: 'Mẫu câu' };
const MODE_LABEL: Record<string, string> = { CHECKPOINT: 'Kiểm tra', PRACTICE: 'Luyện tập' };

export interface WebUnitDialogProps {
  onClose: () => void;
  unit: UnitDetail | null;
  loading: boolean;
  error?: boolean;
  onStartLesson: (lesson: LessonSummary) => void;
  /** Chỉ hiện lesson của track này (dùng khi mở từ node Từ vựng/Ngữ pháp trên Lộ trình).
   * Node "Bài tập" dùng `WebExerciseDialog` riêng, KHÔNG qua component này — xem lý do ở đó. */
  trackFilter?: 'VOCAB' | 'GRAMMAR';
}

/** Dialog Unit — thay cho bottom-sheet của mobile. Danh sách bài lấy từ content API. */
export function WebUnitDialog({ onClose, unit, loading, error, onStartLesson, trackFilter }: WebUnitDialogProps) {
  const lessons = unit && trackFilter ? unit.lessons.filter((l) => l.track === trackFilter) : unit?.lessons;
  const headerLabel = trackFilter ? TRACK_LABEL[trackFilter] : null;

  return (
    <WebModal onClose={onClose} width={720}>
      {loading ? (
        <div style={{ height: 260 }}>
          <LoadingState />
        </div>
      ) : error || !unit || !lessons ? (
        <div style={{ height: 260 }}>
          <ErrorState message="Không tải được danh sách bài của unit này." />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, maxHeight: '88vh' }}>
          <div style={{ padding: '24px 24px 16px', borderBottom: '1px solid var(--border-default)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--navy-300)' }}>
              {headerLabel ? `${headerLabel} · Unit ${unit.order}` : `Unit ${unit.order}`}
            </div>
            <h2 style={{ fontSize: 21, fontWeight: 700, color: 'var(--ink)', margin: '6px 0 0' }}>{unit.name}</h2>
            <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 4 }}>{`${lessons.length} bài`}</div>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {lessons.length === 0 ? (
              <div style={{ padding: 24, fontSize: 14, color: 'var(--text-secondary)', textAlign: 'center' }}>Unit này chưa có bài nào cho nhánh đã chọn.</div>
            ) : (
              lessons.map((lesson, i) => {
                const type = MODE_LABEL[lesson.mode] ?? TRACK_LABEL[lesson.track] ?? lesson.track;
                return (
                  <button
                    key={lesson.id}
                    onClick={() => onStartLesson(lesson)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-vi)',
                      padding: '12px 14px',
                      background: 'var(--surface-page)',
                      border: '1px solid var(--border-default)',
                      borderRadius: 'var(--radius-card)',
                    }}
                  >
                    <span
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: '50%',
                        flexShrink: 0,
                        background: 'var(--surface-tint)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        fontWeight: 700,
                        color: 'var(--cta-default)',
                      }}
                    >
                      {i + 1}
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 14.5, fontWeight: 700, color: 'var(--ink)' }}>{lesson.name}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, minWidth: 0 }}>
                        <span
                          style={{
                            flexShrink: 0,
                            fontSize: 10.5,
                            fontWeight: 700,
                            color: 'var(--cta-default)',
                            background: 'rgba(0,91,150,0.09)',
                            borderRadius: 999,
                            padding: '2px 8px',
                          }}
                        >
                          {type}
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                          {lesson.mode === 'PRACTICE' ? `${lesson.exerciseCount} bài tập` : `${lesson.itemCount} mục`}
                        </span>
                      </span>
                    </span>
                    {WEB_ICO.chev()}
                  </button>
                );
              })
            )}
          </div>

          <div style={{ padding: 16, borderTop: '1px solid var(--border-default)' }}>
            <button
              onClick={onClose}
              style={{
                height: 44,
                padding: '0 20px',
                borderRadius: 'var(--radius-control)',
                background: 'transparent',
                border: '1px solid var(--border-default)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontFamily: 'var(--font-vi)',
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              Đóng
            </button>
          </div>
        </div>
      )}
    </WebModal>
  );
}
